// @flow
import React, {useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {isEqual} from "lodash";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EnvironmentOutlined,
    FastForwardOutlined,
    LoadingOutlined,
    ShopOutlined,
    UndoOutlined,
    ArrowsAltOutlined,
    ShrinkOutlined,
    PrinterOutlined
} from "@ant-design/icons";
import {
    Avatar,
    Col,
    List,
    Popover,
    Row,
    Skeleton,
    Spin,
    Table,
    Typography,
    Image,
    Tooltip,
    Button,
    Modal
} from "antd";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import L from "leaflet";
import {LeafletMapBase} from "../../../_defaultComponents/LeafletMapBase/LeafletMapBase";
import {OrderDetailMarker} from "../../../_defaultComponents/OrderDetailMarker/OrderDetailMarker";
import {OrderDetailPolyline} from "../../../_defaultComponents/OrderDetailPolyline/OrderDetailPolyline";
import {useFetch} from "../../../../hooks/useFetch";
import {useStore} from "../../../../hooks/useStore";
import useIsFocused from "../../../../hooks/useIsFocused";
import {
    AnnulmentButton,
    DescriptionContainer,
    DetailTag,
    FileImageFilledIcon,
    LeftButton,
    MapContainer,
    MapTools,
    Marker,
    MiddleButton,
    MinusCircleFilledIcon,
    OrderNumber,
    OrderTable,
    OrderTotalTime,
    MergedOrderStats,
    RightButton,
    SpinnerContainer,
    StepContainer,
    TableOverflow,
    TableTitle,
    TransferDescription,
    TransferInfoContainer,
    TransferLogList,
    PassButton,
    MapStepRow
} from "./orderDetailStyles";
import {orderDetailPropType, updateOrderPropTypes} from "./orderDetailPropTypes";
import {CourierIcon} from "../../../_defaultComponents/CourierIcon/CourierIcon";
import {
    averageGeolocation,
    decodePolyline,
    formatPhoneNumber,
    calculateElapsedTime
} from "../../../../utils/helpers";
import {
    courierStatusColors,
    orderTableColors,
    pickupColor,
    receiverColor,
    canceledColor,
    deliveredColor,
    defaultSlotCourierIds
} from "../../../../constants/constants";
import {TextBoxFormModal} from "../../../_defaultComponents/TextBoxFormModal/TextBoxFormModal";
import config from "../../../../_core/config";
import placeholder from "../../../../public/images/person-placeholder.jpg";
//import placeholder from "../../../public/images/person-placeholder.jpg";
import {ProofModal} from "./proof/ProofModal";
import ProductTable from "../../../_defaultComponents/ProductTable";
import OrderSteps from "./Steps/OrderSteps";
import SlotDetailCard from "./slot/SlotDetailCard";
import {ReceiptPrinter} from "../../../_defaultComponents/ReceiptPrinter/ReceiptPrinter";
import {TableWrapper} from "../../../_defaultComponents/TableWrapper/TableWrapper";

const {Column} = Table;
const {Text} = Typography;
const initialText = {
    label: "",
    title: ""
};

export const CustomerOrderDetail = ({deliveryId}) => {
    const stores = useStore();
    const history = useHistory();
    const isFocused = useIsFocused();
    const {t, i18n} = useTranslation(["orderDetail", "deliveryStatuses", "paymentTypes"]);
    const [orderSteps, setOrderSteps] = useState([]);
    const [pickupSteps, setPickupSteps] = useState([]);
    const [returnSteps, setReturnSteps] = useState([]);
    const [allSteps, setAllSteps] = useState([]);
    const [canceled, setCanceled] = useState(false);
    const [returned, setReturned] = useState(false);
    const [delivered, setDelivered] = useState(false);
    const [inQueue, setInQueue] = useState(false);
    const [inPrep, setInPrep] = useState(false);
    const [assignedCourierId, setAssignedCourierId] = useState();
    const [recall, setRecall] = useState(true);
    const [transferLogRecall, setTransferLogRecall] = useState(true);
    const [loading, setLoading] = useState(false);
    const [colorIndexList, setColorIndexList] = useState({});
    const [hover, setHover] = useState("");
    const [modalType, setModalType] = useState("");
    const [text, setText] = useState(initialText);
    const [modalVisible, setModalVisible] = useState(false);
    const [submit, setSubmit] = useState();
    const [deliveryType, setDeliveryType] = useState();
    const [modalData, setModalData] = useState({deliveryId: "", orderId: ""});
    const [center, setCenter] = useState({
        lat: 41.0082,
        lng: 28.9784
    });
    const [initialCenter, setInitialCenter] = useState(null);
    const [mapInstance, setMapInstance] = useState();
    const [boundsFitted, setBoundsFitted] = useState(false);
    const [mapMode, setMapMode] = useState("relative");
    const [drawnRoutes, setDrawnRoutes] = useState([]);
    const [estimationPolylines, setEstimationPolylines] = useState([]);
    const [exactPathPolylines, setExactPathPolylines] = useState([]);
    const [totalTime, setTotalTime] = useState("");
    const [deliveredTime, setDeliveredTime] = useState("");
    const [latestLocationId, setLatestLocationId] = useState(null);
    const [exactPath, setExactPath] = useState([]);
    const [proofVisible, setProofVisible] = useState();
    const [proofData, setProofData] = useState({proof: {}, step: {}});
    const [receiptModalVisible, setReceiptModalVisible] = useState(false);
    const recallRef = useRef(recall);
    const transferLogRecallRef = useRef(transferLogRecall);
    const receiptRef = useRef();
    const [isReceiptPrinting, setIsReceiptPrinting] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    recallRef.current = recall;
    transferLogRecallRef.current = transferLogRecall;

    const [orderLoading, orderData, setOrderData, setOrderLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.orderStore.getOrder.bind(null, {
            deliveryId,
            hideError: true
        }),
        dependencies: [recall, deliveryId]
    });

    const [transferLoading, transferData, setTransferData, setTransferLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.orderStore.getTransferLogs.bind(null, {
            deliveryId
        }),
        dependencies: [transferLogRecall, deliveryId]
    });

    const [
        deliveryLocationsDataLoading,
        deliveryLocationsData,
        setDeliveryLocationsData,
        setDeliveryLocationsDataLoading
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getDeliveryLocations.bind(null, {
            deliveryId,
            ...(latestLocationId !== null && {latestLocationId: latestLocationId})
        }),
        dependencies: [recall, deliveryId]
    });

    const orderNumber = orderData?.data?.delivery?.orders.map(x => x.orderNumber).join(", ");

    const estimation = orderData?.data?.estimation;

    useEffect(() => {
        if (orderData.status === 400) {
            history.push("/musteri/siparislerim");
        } else if (isFocused) {
            let timeout = null;
            if (
                orderData?.data?.delivery?.deliveryState !== "delivered" &&
                orderData?.data?.delivery?.deliveryState !== "canceled" &&
                orderData?.data?.delivery?.deliveryState !== "rejected" &&
                orderData?.data?.delivery?.deliveryState !== "returned"
            ) {
                timeout = setTimeout(() => {
                    setRecall(!recallRef.current);
                }, 2000);
            }
            if (deliveryLocationsData.length > 0) {
                setLatestLocationId(deliveryLocationsData.slice(-1).pop().id);
            }

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    useEffect(() => {
        if (assignedCourierId) {
            setTransferLogRecall(!transferLogRecallRef.current);
        }
    }, [assignedCourierId]);

    useEffect(() => {
        setDeliveryType(orderData?.data?.delivery?.deliveryType);
        setTotalTime(orderData?.data?.delivery?.createdAt);
        if (orderData.status !== 200) {
            return;
        }

        if (
            orderData?.data?.delivery?.deliveryState === "canceled" ||
            orderData?.data?.delivery?.deliveryState === "rejected" ||
            orderData?.data?.delivery?.deliveryState === "returned"
        ) {
            const newSteps = orderData?.data?.steps.slice(
                0,
                orderData?.data?.delivery.currentStepIndex
            );
            setAllSteps(newSteps);
        } else {
            setAllSteps(orderData?.data?.steps);
        }

        let orderSteps = [];
        let pickupSteps = [];
        let returnSteps = [];
        let colorIndex = {};
        let index = 0;

        orderData.data.steps
            .sort((prev, curr) => prev.stepIndex - curr.stepIndex)
            .forEach(step => {
                if (step.deliveryState === "onDeliveryAddress") {
                    colorIndex[step.stepInfo._id] = orderTableColors[index];
                    orderSteps.push(step);
                }
                if (step.deliveryState === "onPickUpAddress") {
                    const products = [];
                    step?.stepInfo?.orders?.forEach(order => {
                        products.push(...order.products);
                    });
                    pickupSteps.push({...step, products});
                }
                if (step.deliveryState === "onReturnAddress") {
                    returnSteps.unshift(step);
                }
            });

        setDeliveredTime(orderData?.data?.steps[orderData?.data?.steps.length - 1]?.endDate);
        setColorIndexList(colorIndex);
        setOrderSteps(orderSteps);
        setPickupSteps(pickupSteps);
        setReturnSteps(returnSteps);

        // Calculate center for bounds fitting (all coordinates including courier)
        const coordinates = orderSteps
            .map(step => ({
                latitude: step.stepInfo.receiverLocation.lat,
                longitude: step.stepInfo.receiverLocation.lon
            }))
            .concat(pickupSteps.map(step => step.stepInfo.location));

        // Add courier location if available and not delivered/canceled/returned
        if (
            orderData?.data?.courierLocation &&
            !["delivered", "canceled", "returned"].includes(
                orderData?.data?.delivery?.deliveryState
            )
        ) {
            coordinates.push({
                latitude: orderData.data.courierLocation.location.coordinates[1],
                longitude: orderData.data.courierLocation.location.coordinates[0]
            });
        }

        const centerOfMap = averageGeolocation(coordinates);

        // Set initial center if we don't have one yet
        if (!initialCenter && !isNaN(centerOfMap?.lat) && !isNaN(centerOfMap.lng)) {
            setCenter(centerOfMap);
            setInitialCenter(centerOfMap);
        } else if (pickupSteps.length > 0 && !initialCenter) {
            // Fallback to first pickup location if no center calculated
            const firstPickup = pickupSteps[0];
            if (
                firstPickup?.stepInfo?.location?.latitude &&
                firstPickup?.stepInfo?.location?.longitude
            ) {
                const pickupCenter = {
                    lat: firstPickup.stepInfo.location.latitude,
                    lng: firstPickup.stepInfo.location.longitude
                };
                setCenter(pickupCenter);
                setInitialCenter(pickupCenter);
            }
        } else if (initialCenter && !isNaN(centerOfMap?.lat) && !isNaN(centerOfMap.lng)) {
            // Update center if we already have an initial center but data changed
            setCenter(centerOfMap);
        }
        setCanceled(orderData?.data?.delivery?.deliveryState === "canceled");
        setReturned(orderData?.data?.delivery?.deliveryState === "returned");
        setDelivered(orderData?.data?.delivery?.deliveryState === "delivered");
        setInQueue(orderData?.data?.delivery?.deliveryState === "inQueue");
        setInPrep(orderData?.data?.delivery?.deliveryState === "inPrep");
        setAssignedCourierId(orderData.data?.delivery?.assignedCourier?._id);
    }, [orderData]);

    // Set initial map position only once, then let user control the map
    // This prevents popup closures while ensuring correct initial position
    useEffect(() => {
        if (mapInstance && initialCenter && !boundsFitted) {
            mapInstance.setView([initialCenter.lat, initialCenter.lng], 14);
            setBoundsFitted(true);
        }
    }, [mapInstance, initialCenter, boundsFitted]);

    useEffect(() => {
        if (modalType === "cancel") {
            setText({
                label: t("deliveryModal.cancelReasonLabel", {ns: "common"}),
                title: t("deliveryModal.cancelReasonTitle", {ns: "common"})
            });
            setSubmit(() => cancelDelivery);
        } else if (modalType === "splitOrder") {
            setText({
                label: t("modal.splitOrder.label", {ns: "orderDetail"}),
                title: t("modal.splitOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => splitOrder);
        } else if (modalType === "pass") {
            setText({
                label: t("modal.passOrder.label", {ns: "orderDetail"}),
                title: t("modal.passOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => passOrder);
        } else if (modalType === "cancelOrder") {
            setText({
                label: t("modal.cancelOrder.label", {ns: "orderDetail"}),
                title: t("modal.cancelOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => cancelOrder);
        } else if (modalType === "cancelOrderByNumber") {
            setText({
                label: t("modal.cancelOrder.label", {ns: "orderDetail"}),
                title: t("modal.cancelOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => cancelOrderByNumber);
        } else if (modalType !== "transfer") {
            setModalVisible(false);
        }
    }, [modalType]);

    useEffect(() => {
        window.addEventListener("keydown", handleEsc);
        return () => {
            window.removeEventListener("keydown", handleEsc);
        };
    }, [mapMode]);

    // Close all popups when modals are opened
    useEffect(() => {
        if ((modalVisible || receiptModalVisible || proofVisible) && mapInstance) {
            mapInstance.closePopup();
        }
    }, [modalVisible, receiptModalVisible, proofVisible, mapInstance]);

    const handleEsc = event => {
        if (event.keyCode === 27 && mapMode == "fixed") {
            setMapMode("relative");
        }
    };

    const reOrder = ({deliveryId, orderId}) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.reOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.reOrder({
                    deliveryId,
                    orderId
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
            }
        });
    };

    const passOrder = ({deliveryId, orderId, reason}) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.passOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.passOrder({
                    deliveryId,
                    orderId,
                    reason
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
                setModalVisible(false);
            }
        });
    };

    const cancelOrder = ({
        deliveryId,
        orderId,
        reason,
        shouldSendCallback
    }: updateOrderPropTypes) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("deliveryModal.cancelConfirmContent", {ns: "common"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.cancelOrder({
                    deliveryId,
                    orderId,
                    reason,
                    shouldSendCallback
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
                setModalVisible(false);
            }
        });
    };

    const cancelOrderByNumber = ({
        orderNumber,
        reason,
        resend = false,
        triggerInMinutes,
        shouldSendCallback
    }: updateOrderPropTypes) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.cancelOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                const response = await stores.orderStore.cancelOrderByNumber({
                    orderNumber,
                    cancelText: reason.label,
                    cancelKey: reason.value,
                    resend,
                    triggerInMinutes,
                    shouldSendCallback
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
                setModalVisible(false);
            }
        });
    };

    const splitOrder = ({
        deliveryId,
        orderIds,
        allowMerge,
        forceCourierId,
        reason,
        triggerInMinutes
    }) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.splitOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.splitOrder({
                    deliveryId,
                    orderIds,
                    allowMerge,
                    forceCourierId,
                    reason,
                    triggerInMinutes
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
                setModalVisible(false);
            }
        });
    };

    const updateStep = async () => {
        setLoading(true);

        const currentStep =
            allSteps[
                orderData.data?.steps?.slice(-1)[0]?.endDate && delivered
                    ? orderData.data.delivery.currentStepIndex
                    : orderData.data.delivery.currentStepIndex - 1
            ];

        if (
            currentStep.deliveryState === "onDeliveryAddress" &&
            Boolean(currentStep.startDate) &&
            Boolean(currentStep.trial)
        ) {
            if (currentStep.trial.startDate) {
                await stores.orderStore.trialEnd({courierId: assignedCourierId});
                await stores.orderStore.updateStep({
                    deliveryId,
                    courierId: orderData.data?.delivery?.assignedCourier?._id
                });
            } else {
                await stores.orderStore.trialStart({courierId: assignedCourierId});
            }
        } else {
            await stores.orderStore.updateStep({
                deliveryId,
                courierId: orderData.data?.delivery?.assignedCourier?._id
            });
        }

        setRecall(!recallRef.current);
        setLoading(false);
    };

    const cancelDelivery = ({reason, shouldSendCallback}: deliveryUpdateWithTextPropType) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("deliveryModal.cancelConfirmContent", {ns: "common"}),
            onOk: async () => {
                await setOrderLoading(true);
                stores.orderStore.cancelDelivery({
                    cancelText: reason.label,
                    cancelKey: reason.value,
                    deliveryId,
                    shouldSendCallback
                });
                setRecall(!recallRef.current);
                setModalVisible(false);
                await setOrderLoading(false);
            }
        });
    };

    /* const repeatDelivery = async () => {
        const company = await stores.companyStore.getCompany({
            companyId: stores.meStore.company._id,
            withSubCompanies: false
        });
        const auth = await stores.apiRequestStore.getAuthToken({
            data: {apiSecret: company.data.apiSecret},
            apiKey: company.data.apiKey
        });
        const body = {
            repeatDelivery: true,
            pickupInfos: orderData.data.delivery.pickupInfos.map((info) => {
                const {index, name, phone, address, location} = info;

                return {index, name, phone, address, location};
            }),
            orders: orderData.data.delivery.orders.map((order, i) => {
                if (!order.orderNumber) {
                    order.orderNumber = moment().format("mmssSSS");
                }

                let {
                    index,
                    products,
                    receiverName,
                    receiverAddressDetail,
                    receiverAddress,
                    receiverLocation,
                    receiverPhone,
                    orderNumber
                } = order;
                return {
                    index,
                    products,
                    receiverName,
                    receiverAddressDetail,
                    receiverAddress,
                    receiverLocation,
                    receiverPhone,
                    orderNumber
                };
            }),
            courierType: orderData.data.delivery.courierType
        };
        await stores.apiRequestStore.createDelivery({
            data: body,
            apiKey: company.data.apiKey,
            token: auth.token
        });
    }; */

    const fitBounds = ({map, pickupSteps, orderSteps, courierLocation, estimationRoutes}) => {
        if (!map) return;

        const bounds = [];

        if (pickupSteps) {
            pickupSteps.map(pickup => {
                if (pickup?.stepInfo?.location?.latitude && pickup?.stepInfo?.location?.longitude) {
                    bounds.push([
                        pickup.stepInfo.location.latitude,
                        pickup.stepInfo.location.longitude
                    ]);
                }
            });
        }

        if (orderSteps) {
            orderSteps.map(order => {
                if (
                    order?.stepInfo?.receiverLocation?.lat &&
                    order?.stepInfo?.receiverLocation?.lng
                ) {
                    bounds.push([
                        order.stepInfo.receiverLocation.lat,
                        order.stepInfo.receiverLocation.lng
                    ]);
                }
            });
        }

        if (
            courierLocation &&
            !["delivered", "canceled", "returned"].includes(
                orderData?.data?.delivery?.deliveryState
            )
        ) {
            bounds.push([courierLocation.coordinates[1], courierLocation.coordinates[0]]);
        }

        if (estimationRoutes) {
            estimationRoutes
                .filter(r => !!r.encodedPolylines)
                .map(route => {
                    const path = decodePolyline(route.encodedPolylines);
                    path.map(coordinate => {
                        bounds.push([coordinate.lat, coordinate.lng]);
                    });
                });
        }

        if (bounds.length === 0) return;

        // Fit bounds using Leaflet
        const group = new L.featureGroup();
        bounds.forEach(coord => {
            group.addLayer(L.marker(coord));
        });
        map.fitBounds(group.getBounds().pad(0.1));
    };

    useEffect(() => {
        if (estimation?.routes && mapInstance) {
            const polylines = [];
            for (let i = 0; i < estimation.routes?.length; i++) {
                if (!estimation.routes[i].encodedPolylines) {
                    continue;
                }
                let routeTopColor;
                if (i < estimation.routes.length - 1) {
                    if (estimation.routes[i + 1].routeType === "fromDelivery")
                        routeTopColor = "#ff9966";
                    // Orange for courier to customer
                    else if (estimation.routes[i + 1].routeType === "fromPickup")
                        routeTopColor = "#5abeea"; // Blue for depot to courier
                } else {
                    routeTopColor = "#ff9966"; // Orange for final route
                }
                polylines.push({
                    encodedPolylines: estimation.routes[i].encodedPolylines,
                    strokeColor: routeTopColor,
                    strokeWeight: 4
                });
            }
            setEstimationPolylines(polylines);
        }
    }, [estimation?.routes, mapInstance]);

    useEffect(() => {
        if (deliveryLocationsData.length > 0 && mapInstance) {
            const newLocations = [];
            deliveryLocationsData.forEach(item =>
                newLocations.push({lat: item.coordinates[1], lng: item.coordinates[0]})
            );
            if (
                newLocations.length === 1 &&
                isEqual(exactPath[exactPath.length - 1], newLocations[0])
            ) {
                return;
            }
            setExactPath(exactPath => exactPath.concat(newLocations));

            // Create exact path polylines
            const exactPolylines = [
                {
                    path: exactPath,
                    strokeColor: "#54E626", // Green for exact path
                    strokeWeight: 4
                }
            ];
            setExactPathPolylines(exactPolylines);
        }
    }, [deliveryLocationsData, mapInstance]);

    const renderProofIcon = step => {
        let {proofs} = step;
        proofs = proofs.filter(p => p.label == proofs[0].label);
        let proofIcons = null;
        const emptyProof = () => <MinusCircleFilledIcon color={courierStatusColors.unavailable} />;
        const filledProof = () => {
            return (
                <>
                    {proofs.map(proof => {
                        if (
                            (step.deliveryState === "onDeliveryAddress" &&
                                step.stepInfo.orderNumber === proof.orderNumber) ||
                            (step.deliveryState === "onPickUpAddress" &&
                                step.stepInfo.orders.filter(
                                    order => order.orderNumber === proof.orderNumber
                                ).length > 0)
                        ) {
                            return (
                                <FileImageFilledIcon
                                    key={proof?._id}
                                    onClick={() => {
                                        const step =
                                            allSteps.filter(
                                                step => step?.stepIndex === proof?.stepIndex
                                            )[0] || {};
                                        const receiverName = getReceiverName(proof.orderNumber);
                                        setProofData({
                                            proof,
                                            step: {...step, receiverName}
                                        });
                                        setProofVisible(step.deliveryState);
                                    }}
                                    color={courierStatusColors.available}
                                />
                            );
                        }
                    })}
                </>
            );
        };
        const getPickUpAddress = state => allSteps.filter(step => step?.deliveryState === state);
        const getDeliveryAddress = (state, orderNumber) =>
            allSteps.filter(
                step => step?.stepInfo?.orderNumber === orderNumber && step?.deliveryState === state
            );
        const getReceiverName = orderNumber =>
            getDeliveryAddress("onDeliveryAddress", orderNumber)[0]?.stepInfo?.receiverName;
        const getProof = (step, orderNumber) =>
            step?.proofs.filter(proof => proof.orderNumber === orderNumber)[0];

        if (proofs) {
            proofIcons = filledProof();
        } else {
            proofIcons = emptyProof();
        }

        return (
            <>
                {proofIcons}
                {proofVisible == step.deliveryState ? (
                    <ProofModal
                        visible={proofVisible == step.deliveryState}
                        data={proofData}
                        handleClose={() => setProofVisible(null)}
                        changeDeliveryState={(orderNumber, deliveryState) => {
                            const selectedStep =
                                deliveryState === "onPickUpAddress"
                                    ? getPickUpAddress(deliveryState)
                                    : getDeliveryAddress(deliveryState, orderNumber);
                            const receiverName = getReceiverName(orderNumber);
                            setProofData({
                                proof: getProof(selectedStep[0], orderNumber),
                                step: {...selectedStep[0], receiverName},
                                orderNumber
                            });
                        }}
                    />
                ) : null}
            </>
        );
    };

    const renderMergedOrderStats = () => {
        if (
            delivered ||
            canceled ||
            returned ||
            inPrep ||
            inQueue ||
            orderData?.data?.delivery?.orders?.length < 2
        ) {
            return null;
        }
        let content = "";
        //active orders and merged orders
        let totalOrdersCount = orderData?.data?.delivery?.orders?.length || 0;
        let deliveredOrdersCount = 0;
        let pickedUpOrdersCount = 0;

        orderData.data?.delivery?.orders?.forEach(order => {
            const hasPickedUp = orderData?.data?.delivery?.pickupInfos?.some(
                pI => pI._id.toString() === order.pickupInfoId.toString() && pI.state === "picked"
            );
            if (hasPickedUp) {
                pickedUpOrdersCount++;
            }

            if (order.state === "delivered") {
                deliveredOrdersCount++;
            }
        });

        if (pickedUpOrdersCount > 0 || deliveredOrdersCount > 0) {
            content = (() => (
                <>
                    {t("mergedOrdersStatus.picked", {
                        ns: "orderDetail",
                        pickedUpCount: pickedUpOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                    ,
                    <br />
                    {t("mergedOrdersStatus.delivered", {
                        ns: "orderDetail",
                        deliveredCount: deliveredOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                </>
            ))();
        } else {
            return null;
        }

        return (
            <>
                <MergedOrderStats>{content}</MergedOrderStats>
                <br />
            </>
        );
    };

    const toggleMapMode = () => {
        if (mapMode == "fixed") {
            setMapMode("relative");
        } else {
            setMapMode("fixed");
        }
    };

    const courierIconBackgroundColor = useMemo(() => {
        let colorStatueName = orderData.data?.courierLocation?.status;
        if (colorStatueName === "busy") {
            colorStatueName = orderData.data.delivery.deliveryState;
        }
        return courierStatusColors[colorStatueName];
    }, [orderData?.data?.courierLocation?.status, orderData?.data?.delivery?.deliveryState]);

    return (
        <Row style={{width: "100%"}}>
            {!orderData.data || orderData.status !== 200 ? (
                <Skeleton paragraph={{rows: 10, width: "100%"}} active={true}></Skeleton>
            ) : (
                <React.Fragment>
                    <TextBoxFormModal
                        isVisible={modalVisible}
                        setVisible={() => setModalVisible(!modalVisible)}
                        okFunction={submit}
                        label={text.label}
                        title={text.title}
                        modalType={modalType}
                        modalData={modalData}
                        deliveryId={deliveryId}
                        deliveryData={orderData.data}
                        allowMergeDefault
                        cancelFn={() => {
                            setModalType("");
                        }}
                        deliveryType={deliveryType}
                        allPoolCouriers={true}
                        isCustomer={true}
                    />

                    <Row
                        gutter={[
                            {xs: 12, sm: 16, md: 20, lg: 30},
                            {xs: 12, sm: 16, md: 20, lg: 30}
                        ]}
                        data-testid="orderDetails"
                    >
                        <Col span={24}>
                            <OrderNumber data-testid="orderNumber">
                                {t("fields.orderNumber", {ns: "common"})}:{" "}
                                {orderNumber ? orderNumber : "-"}
                            </OrderNumber>
                            <MapStepRow
                                gutter={[
                                    {xs: 12, sm: 16, md: 20, lg: 30},
                                    {xs: 12, sm: 16, md: 20, lg: 30}
                                ]}
                            >
                                <Col xs={24} sm={24} md={24} lg={10} data-test-id="steps-section">
                                    {orderData.data?.delivery.customType == "slot" && (
                                        <SlotDetailCard orderData={orderData}></SlotDetailCard>
                                    )}
                                    {/*@TODO: split this component  */}
                                    <StepContainer
                                        data-testid="orderDetailCard"
                                        data-testvalue={
                                            orderData.data?.steps?.slice(-1)[0]?.endDate &&
                                            delivered
                                                ? orderData.data.delivery.currentStepIndex
                                                : orderData.data.delivery.currentStepIndex - 1
                                        }
                                    >
                                        <TableTitle>
                                            {deliveryType === "productReturn"
                                                ? t("returnDetail", {ns: "orderDetail"})
                                                : t("orderDetail", {ns: "orderDetail"})}
                                            {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                                !delivered &&
                                                !canceled &&
                                                !returned &&
                                                (process.env.REACT_APP_ENV === "development" ||
                                                    process.env.REACT_APP_ENV === "staging" ||
                                                    stores.meStore.role == "admin" ||
                                                    stores.meStore.role === "superadmin" ||
                                                    stores.meStore.role === "bm" ||
                                                    stores.meStore.role === "agency") && (
                                                    <RightButton
                                                        disabled={loading}
                                                        onClick={updateStep}
                                                        data-testid="nextStepButton"
                                                        icon={
                                                            loading ? (
                                                                <LoadingOutlined />
                                                            ) : (
                                                                <FastForwardOutlined />
                                                            )
                                                        }
                                                    >
                                                        {loading
                                                            ? t("loading", {ns: "orderDetail"})
                                                            : t("actions.nextStep", {
                                                                  ns: "orderDetail"
                                                              })}
                                                    </RightButton>
                                                )}
                                        </TableTitle>
                                        {loading && (
                                            <SpinnerContainer>
                                                <Spin spinning={loading} />
                                            </SpinnerContainer>
                                        )}
                                        <OrderSteps
                                            orderData={orderData}
                                            transferData={transferData}
                                            renderProofIcon={renderProofIcon}
                                        />
                                        {renderMergedOrderStats()}
                                        <OrderTotalTime>
                                            {t("fields.elapsedTime", {ns: "common"})}:{" "}
                                            <span data-testid="deliveredTime" />
                                            {delivered
                                                ? calculateElapsedTime(totalTime, deliveredTime)
                                                : canceled
                                                ? t("canceled.shortName", {ns: "deliveryStatuses"})
                                                : calculateElapsedTime(totalTime, new Date())}
                                        </OrderTotalTime>
                                    </StepContainer>
                                </Col>
                                <Col xs={24} sm={24} md={24} lg={14} data-test-id="map-section">
                                    <Row gutter={[0, {xs: 16, sm: 20, md: 30}]}>
                                        <MapContainer mode={mapMode} data-testid="map">
                                            <MapTools
                                                onClick={e => {
                                                    toggleMapMode();
                                                    e.currentTarget.blur();
                                                }}
                                            >
                                                {mapMode == "relative" ? (
                                                    <ArrowsAltOutlined />
                                                ) : (
                                                    <ShrinkOutlined />
                                                )}
                                            </MapTools>
                                            <LeafletMapBase
                                                center={center}
                                                zoom={14}
                                                onMapReady={map => {
                                                    setMapInstance(map);
                                                    // Initial view will be set by useEffect when data is ready
                                                }}
                                                style={{width: "100%", height: "100%"}}
                                            >
                                                {/* Render estimation polylines */}
                                                {estimationPolylines.map((polyline, index) => (
                                                    <OrderDetailPolyline
                                                        key={`estimation-${index}`}
                                                        mapInstance={mapInstance}
                                                        encodedPolylines={polyline.encodedPolylines}
                                                        strokeColor={polyline.strokeColor}
                                                        strokeWeight={polyline.strokeWeight}
                                                        isVisible={true}
                                                    />
                                                ))}

                                                {/* Render exact path polylines */}
                                                {exactPathPolylines.map((polyline, index) => (
                                                    <OrderDetailPolyline
                                                        key={`exact-${index}`}
                                                        mapInstance={mapInstance}
                                                        path={polyline.path}
                                                        strokeColor={polyline.strokeColor}
                                                        strokeWeight={polyline.strokeWeight}
                                                        isVisible={true}
                                                    />
                                                ))}

                                                {/* Courier marker */}
                                                {!defaultSlotCourierIds.includes(
                                                    orderData.data?.delivery?.assignedCourier?._id?.toString()
                                                ) &&
                                                    !["delivered", "canceled", "returned"].includes(
                                                        orderData?.data?.delivery?.deliveryState
                                                    ) &&
                                                    orderData.data.courierLocation && (
                                                        <OrderDetailMarker
                                                            mapInstance={mapInstance}
                                                            lat={
                                                                orderData.data.courierLocation
                                                                    .location.coordinates[1]
                                                            }
                                                            lng={
                                                                orderData.data.courierLocation
                                                                    .location.coordinates[0]
                                                            }
                                                            icon={
                                                                <CourierIcon
                                                                    courierType={
                                                                        orderData.data
                                                                            .courierLocation.type
                                                                    }
                                                                />
                                                            }
                                                            text={`${t("fields.courier", {
                                                                ns: "common"
                                                            })}: ${
                                                                orderData.data?.delivery
                                                                    ?.assignedCourier?.fullname
                                                            }`}
                                                            color={courierIconBackgroundColor}
                                                            size={24}
                                                            type="courier"
                                                        />
                                                    )}

                                                {/* Pickup markers */}
                                                {pickupSteps.map((pickup, index) => (
                                                    <OrderDetailMarker
                                                        key={`pickup-${index}`}
                                                        mapInstance={mapInstance}
                                                        lat={pickup?.stepInfo?.location?.latitude}
                                                        lng={pickup?.stepInfo?.location?.longitude}
                                                        icon={<ShopOutlined />}
                                                        text={`${t("fields.warehouse", {
                                                            ns: "common"
                                                        })}: ${pickup?.stepInfo?.name}`}
                                                        color={pickupColor}
                                                        size={22}
                                                        type="pickup"
                                                    />
                                                ))}

                                                {/* Receiver markers */}
                                                {orderSteps.map((order, index) => {
                                                    const orders =
                                                        orderData?.data?.delivery?.orders;
                                                    const orderItem =
                                                        orders.length > 0 &&
                                                        orders.find(
                                                            o =>
                                                                o.orderNumber ==
                                                                order.stepInfo?.orderNumber
                                                        );
                                                    let color = receiverColor;
                                                    switch (orderItem?.state) {
                                                        case "canceled":
                                                        case "returned":
                                                            color = canceledColor;
                                                            break;
                                                        case "delivered":
                                                            color = deliveredColor;
                                                            break;
                                                        default:
                                                            color = receiverColor;
                                                    }

                                                    return (
                                                        <OrderDetailMarker
                                                            key={`order-${index}`}
                                                            mapInstance={mapInstance}
                                                            lat={
                                                                order?.stepInfo?.receiverLocation
                                                                    ?.lat
                                                            }
                                                            lng={
                                                                order?.stepInfo?.receiverLocation
                                                                    ?.lon
                                                            }
                                                            icon={<EnvironmentOutlined />}
                                                            text={`${t("fields.customer", {
                                                                ns: "common"
                                                            })}: ${order?.stepInfo?.receiverName ||
                                                                order?.stepInfo?.receiptNumber}`}
                                                            color={color}
                                                            size={22}
                                                            type="receiver"
                                                        />
                                                    );
                                                })}
                                            </LeafletMapBase>
                                        </MapContainer>
                                        <Col span={24} data-test-id="order-detail-section">
                                            <TableTitle>
                                                Siparişler
                                                {/*  {deliveryType === "productReturn"
                                    ? t("fields.returnPoints", {ns: "common"})
                                    : t("fields.deliveryPoints", {ns: "common"})} */}
                                            </TableTitle>

                                            <TableWrapper
                                                data-testid="orderTable"
                                                onRow={(record, index) => {
                                                    return {
                                                        onMouseEnter: event => {
                                                            setHover(record.stepInfo._id);
                                                        },
                                                        style: {
                                                            backgroundColor:
                                                                (record.stepInfo.passed &&
                                                                    (hover === record.stepInfo._id
                                                                        ? colorIndexList[
                                                                              record.stepInfo._id
                                                                          ]
                                                                        : "white")) ||
                                                                (record.reorderOf &&
                                                                    (hover === record.stepInfo._id
                                                                        ? colorIndexList[
                                                                              record.stepInfo._id
                                                                          ]
                                                                        : "white"))
                                                        },
                                                        onMouseLeave: event => {
                                                            setHover("");
                                                        }
                                                    };
                                                }}
                                                rowKey="_id"
                                                dataSource={orderSteps}
                                                pagination={false}
                                                expandable={{
                                                    expandedRowRender: record => {
                                                        const order = orderData?.data?.delivery?.orders.filter(
                                                            i =>
                                                                i?.orderNumber ===
                                                                record?.stepInfo?.orderNumber
                                                        );

                                                        return (
                                                            <ProductTable
                                                                showNote
                                                                dataSource={
                                                                    order
                                                                        ? order?.[0]?.products
                                                                        : []
                                                                }
                                                                pagination={false}
                                                                note={record.stepInfo.note}
                                                                price={record.stepInfo.price}
                                                                paymentType={
                                                                    record.stepInfo.paymentType
                                                                }
                                                                showPaymentInfo={true}
                                                                barcode={record.stepInfo.barcode}
                                                                showBarcode={true}
                                                            />
                                                        );
                                                    }
                                                }}
                                            >
                                                <Column
                                                    dataIndex="stepInfo"
                                                    title={t("fields.warehouseName", {
                                                        ns: "common"
                                                    })}
                                                    render={value => {
                                                        const orders =
                                                            orderData?.data?.delivery?.orders || [];
                                                        const order = orders?.find(
                                                            order => order?._id === value?._id
                                                        );
                                                        const tooltipTitle =
                                                            "Firma: " +
                                                            order?.pickupInfoCompanyName +
                                                            " - " +
                                                            "Şube: " +
                                                            order?.pickupInfoName;

                                                        const companyName =
                                                            order?.pickupInfoCompanyName;
                                                        return (
                                                            <Tooltip title={tooltipTitle}>
                                                                <span data-testid="receiverCompany">
                                                                    {companyName
                                                                        ? companyName + "..."
                                                                        : ""}
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    }}
                                                />
                                                <Column
                                                    dataIndex="stepInfo"
                                                    title={
                                                        deliveryType === "productReturn"
                                                            ? t("fields.returnPoint", {
                                                                  ns: "common"
                                                              })
                                                            : t(
                                                                  "fields.customerNameOrReceiptNumber",
                                                                  {
                                                                      ns: "common"
                                                                  }
                                                              )
                                                    }
                                                    render={text => (
                                                        <span data-testid="receiverName">
                                                            {text.receiverName ||
                                                                text.receiptNumber}
                                                        </span>
                                                    )}
                                                />
                                                <Column
                                                    dataIndex="stepInfo"
                                                    title={t("fields.phone", {ns: "common"})}
                                                    render={text => (
                                                        <span data-testid="receiverPhone">
                                                            {formatPhoneNumber(
                                                                text.receiverPhone,
                                                                text.receiverPhone
                                                            )}
                                                        </span>
                                                    )}
                                                />
                                                <Column
                                                    dataIndex="stepInfo"
                                                    title={t("fields.addressDetail", {
                                                        ns: "common"
                                                    })}
                                                    render={stepInfo => (
                                                        <span data-testid="receiverAddressDetail">
                                                            {stepInfo.receiverAddressDetail}
                                                        </span>
                                                    )}
                                                />
                                                <Column
                                                    dataIndex="stepInfo"
                                                    title={"Mesafe Bilgisi"}
                                                    render={stepInfo => (
                                                        <Tooltip
                                                            title={stepInfo.distance + " metre"}
                                                        >
                                                            <span
                                                                data-testid="distance"
                                                                style={{
                                                                    borderBottom: "1px dotted #999"
                                                                }}
                                                            >
                                                                {stepInfo.distance
                                                                    ? (
                                                                          stepInfo.distance / 1000
                                                                      ).toFixed(2) + " km"
                                                                    : "-"}
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                />
                                                <Column
                                                    dataIndex="stepInfo"
                                                    title={"Sipariş Tutarı"}
                                                    render={text => {
                                                        return (
                                                            <span data-testid="orderPrice">
                                                                {text?.prices
                                                                    ?.map(price => price?.price)
                                                                    .reduce((a, b) => a + b, 0) +
                                                                    " ₺"}
                                                            </span>
                                                        );
                                                    }}
                                                />
                                                <Column
                                                    dataIndex="stepInfo"
                                                    title={"Not"}
                                                    render={text => {
                                                        const orders =
                                                            orderData?.data?.delivery?.orders || [];
                                                        const order = orders?.find(
                                                            order => order?._id === text._id
                                                        );
                                                        return (
                                                            <span data-testid="operationNote">
                                                                {order?.operationNote || ""}
                                                            </span>
                                                        );
                                                    }}
                                                />

                                                {orderData?.data?.delivery?.orders.some(
                                                    order => order?.receipt !== null
                                                ) ? (
                                                    <Column
                                                        dataIndex="stepInfo"
                                                        title={t("fields.orderReceipt", {
                                                            ns: "common"
                                                        })}
                                                        render={stepInfo => {
                                                            const receipt = orderData?.data?.delivery?.orders.find(
                                                                o =>
                                                                    o._id.toString() ===
                                                                    stepInfo._id
                                                            )?.receipt;

                                                            return (
                                                                <Image
                                                                    src={receipt?.publicUrl}
                                                                    width={200}
                                                                    height={200}
                                                                    style={{
                                                                        objectFit: "contain"
                                                                    }}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                ) : null}

                                                {stores.meStore.checkRole("updateOrder") &&
                                                    orderData.data.delivery.orders.length > 0 && (
                                                        <Column
                                                            align="center"
                                                            dataIndex="stepInfo"
                                                            title={t("actions.action", {
                                                                ns: "common"
                                                            })}
                                                            render={(text, row) => {
                                                                const currentStepIndex =
                                                                    orderData.data.delivery
                                                                        .currentStepIndex;
                                                                const lastOrderStepIndex =
                                                                    orderSteps[
                                                                        orderSteps.length - 1
                                                                    ].stepIndex;

                                                                const passButton =
                                                                    row.stepIndex ===
                                                                        currentStepIndex &&
                                                                    currentStepIndex !==
                                                                        lastOrderStepIndex;
                                                                const reOrderButton =
                                                                    row.stepInfo.passed &&
                                                                    !row.stepInfo.reOrdered;
                                                                const isReordered =
                                                                    orderData.data?.delivery?.orders.find(
                                                                        o =>
                                                                            o._id.toString() ===
                                                                            text._id
                                                                    )?.state === "reOrdered";

                                                                const disabled =
                                                                    delivered ||
                                                                    canceled ||
                                                                    orderData.data.delivery
                                                                        .deliveryState ===
                                                                        "onTheWayForReturn" ||
                                                                    orderData.data.delivery
                                                                        .deliveryState ===
                                                                        "onReturnAddress";

                                                                const orders =
                                                                    orderData?.data?.delivery
                                                                        ?.orders || [];
                                                                const order = orders?.find(
                                                                    order =>
                                                                        order?._id ===
                                                                        row?.stepInfo?._id
                                                                );

                                                                return (
                                                                    <Row>
                                                                        {row.stepInfo.passed && (
                                                                            <Col span={24}>
                                                                                <Popover
                                                                                    content={
                                                                                        <span>
                                                                                            {row
                                                                                                .stepInfo
                                                                                                .passReason
                                                                                                ? row
                                                                                                      .stepInfo
                                                                                                      .passReason
                                                                                                : t(
                                                                                                      "passReasonProblem",
                                                                                                      {
                                                                                                          ns:
                                                                                                              "orderDetail"
                                                                                                      }
                                                                                                  )}
                                                                                        </span>
                                                                                    }
                                                                                    title={t(
                                                                                        "reason",
                                                                                        {
                                                                                            ns:
                                                                                                "orderDetail"
                                                                                        }
                                                                                    )}
                                                                                    placement="rightTop"
                                                                                    trigger="hover"
                                                                                >
                                                                                    <DetailTag
                                                                                        icon={
                                                                                            <CheckCircleOutlined />
                                                                                        }
                                                                                        color="success"
                                                                                    >
                                                                                        {t(
                                                                                            "passed",
                                                                                            {
                                                                                                ns:
                                                                                                    "orderDetail"
                                                                                            }
                                                                                        )}
                                                                                    </DetailTag>
                                                                                </Popover>
                                                                            </Col>
                                                                        )}
                                                                        <Col span={24}>
                                                                            {stores.meStore.checkRole(
                                                                                "passOrder"
                                                                            ) ? (
                                                                                <PassButton
                                                                                    data-testid={`${
                                                                                        reOrderButton
                                                                                            ? "sendAgain"
                                                                                            : "pass"
                                                                                    }Button`}
                                                                                    disabled={
                                                                                        (!reOrderButton &&
                                                                                            !passButton) ||
                                                                                        isReordered ||
                                                                                        disabled
                                                                                    }
                                                                                    onClick={() => {
                                                                                        if (
                                                                                            reOrderButton
                                                                                        ) {
                                                                                            reOrder(
                                                                                                {
                                                                                                    deliveryId,
                                                                                                    orderId:
                                                                                                        text._id
                                                                                                }
                                                                                            );
                                                                                        }
                                                                                        if (
                                                                                            passButton
                                                                                        ) {
                                                                                            setModalVisible(
                                                                                                !modalVisible
                                                                                            );
                                                                                            setModalType(
                                                                                                "pass"
                                                                                            );
                                                                                            setModalData(
                                                                                                {
                                                                                                    deliveryId,
                                                                                                    orderId:
                                                                                                        text._id
                                                                                                }
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {reOrderButton
                                                                                        ? t(
                                                                                              "actions.sendAgain",
                                                                                              {
                                                                                                  ns:
                                                                                                      "orderDetail"
                                                                                              }
                                                                                          )
                                                                                        : t(
                                                                                              "actions.pass",
                                                                                              {
                                                                                                  ns:
                                                                                                      "orderDetail"
                                                                                              }
                                                                                          )}
                                                                                </PassButton>
                                                                            ) : (
                                                                                <></>
                                                                            )}
                                                                            <Row
                                                                                gutter={[
                                                                                    {xs: 8, sm: 10},
                                                                                    {xs: 8, sm: 10}
                                                                                ]}
                                                                            >
                                                                                <Col>
                                                                                    <Button
                                                                                        type="primary"
                                                                                        onClick={() => {
                                                                                            setSelectedOrder(
                                                                                                order
                                                                                            );
                                                                                            setReceiptModalVisible(
                                                                                                true
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        Fişi
                                                                                        görüntüle
                                                                                    </Button>
                                                                                </Col>
                                                                                <Col>
                                                                                    <AnnulmentButton
                                                                                        type="danger"
                                                                                        data-testid="cancelButton"
                                                                                        disabled={
                                                                                            row
                                                                                                .stepInfo
                                                                                                .canceled ||
                                                                                            orderData
                                                                                                .data
                                                                                                .delivery
                                                                                                .currentStepIndex >
                                                                                                row.stepIndex ||
                                                                                            [
                                                                                                "delivered",
                                                                                                "canceled"
                                                                                            ].includes(
                                                                                                orderData
                                                                                                    .data
                                                                                                    .delivery
                                                                                                    .deliveryState
                                                                                            )
                                                                                        }
                                                                                        onClick={() => {
                                                                                            setModalVisible(
                                                                                                !modalVisible
                                                                                            );
                                                                                            setModalType(
                                                                                                "cancelOrderByNumber"
                                                                                            );
                                                                                            setModalData(
                                                                                                {
                                                                                                    orderNumber:
                                                                                                        text.orderNumber
                                                                                                }
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        {t(
                                                                                            "actions.cancel",
                                                                                            {
                                                                                                ns:
                                                                                                    "orderDetail"
                                                                                            }
                                                                                        )}
                                                                                    </AnnulmentButton>
                                                                                </Col>
                                                                            </Row>
                                                                        </Col>
                                                                    </Row>
                                                                );
                                                            }}
                                                        />
                                                    )}
                                            </TableWrapper>
                                            <ReceiptPrinter
                                                receiptModalVisible={receiptModalVisible}
                                                setReceiptModalVisible={setReceiptModalVisible}
                                                order={selectedOrder}
                                                orderData={orderData}
                                                receiptRef={receiptRef}
                                                setIsReceiptPrinting={setIsReceiptPrinting}
                                                isReceiptPrinting={isReceiptPrinting}
                                            />

                                            <Row
                                                justify="end"
                                                gutter={[
                                                    {xs: 8, sm: 12, md: 16},
                                                    {xs: 8, sm: 12, md: 16}
                                                ]}
                                                data-testid="actionButtons"
                                                style={{marginTop: "16px"}}
                                            >
                                                {!delivered &&
                                                    !canceled &&
                                                    ((stores.meStore.checkRole(
                                                        "canSplitDeliveries"
                                                    ) &&
                                                        orderData?.data?.delivery
                                                            ?.integrationType ===
                                                            "dedicatedIntegration") ||
                                                        stores.meStore.subcompanies.some(
                                                            subcompany =>
                                                                subcompany.subCompanyWorkType ===
                                                                "dedicated"
                                                        )) && (
                                                        <Row>
                                                            <LeftButton
                                                                style={{
                                                                    marginRight: "50px",
                                                                    paddingRight: "25px",
                                                                    paddingLeft: "25px",
                                                                    marginTop: "25px"
                                                                }}
                                                                data-testid="splitButton"
                                                                type="primary"
                                                                onClick={() => {
                                                                    setModalVisible(!modalVisible);
                                                                    setModalType("splitOrder");
                                                                    setModalData({
                                                                        deliveryId,
                                                                        orderSteps
                                                                    });
                                                                }}
                                                            >
                                                                {t("actions.splitOrder", {
                                                                    ns: "orderDetail"
                                                                })}
                                                            </LeftButton>
                                                        </Row>
                                                    )}
                                                {stores.meStore.checkRole("transferOrder") &&
                                                    !delivered &&
                                                    !canceled &&
                                                    !returned && (
                                                        <Row>
                                                            <MiddleButton
                                                                type="primary"
                                                                data-testid="transferButton"
                                                                onClick={() => {
                                                                    setModalVisible(!modalVisible);
                                                                    setModalType("transfer");
                                                                }}
                                                                icon={<CloseCircleOutlined />}
                                                            >
                                                                {t("actions.transferOrder", {
                                                                    ns: "orderDetail"
                                                                })}
                                                            </MiddleButton>
                                                        </Row>
                                                    )}

                                                {stores.meStore.checkRole("cancelRepeatOrder") &&
                                                    !delivered &&
                                                    !canceled &&
                                                    !returned && (
                                                        <Row>
                                                            <RightButton
                                                                type="danger"
                                                                data-testid="cancelButton"
                                                                onClick={() => {
                                                                    setModalVisible(!modalVisible);
                                                                    setModalType("cancel");
                                                                    setModalData({
                                                                        deliveryId,
                                                                        orderId: text._id
                                                                    });
                                                                }}
                                                                icon={<CloseCircleOutlined />}
                                                                style={{
                                                                    marginRight: "50px",
                                                                    paddingRight: "25px",
                                                                    paddingLeft: "25px",
                                                                    marginTop: "25px"
                                                                }}
                                                            >
                                                                {t("actions.cancelOrder", {
                                                                    ns: "common"
                                                                })}
                                                            </RightButton>
                                                        </Row>
                                                    )}
                                                {stores.meStore.checkRole("cancelRepeatOrder") &&
                                                    (delivered || canceled || returned) && (
                                                        <Row>
                                                            <RightButton
                                                                type="primary"
                                                                data-testid="repeatButton"
                                                                onClick={() => {
                                                                    history.push(
                                                                        "/third-party-request/new/" +
                                                                            deliveryId
                                                                    );
                                                                }}
                                                                icon={<UndoOutlined />}
                                                            >
                                                                {t("actions.resendOrder", {
                                                                    ns: "orderDetail"
                                                                })}
                                                            </RightButton>
                                                        </Row>
                                                    )}
                                            </Row>
                                            {/* Geçiçi Olarak Comment-out <>
                                <TableTitle>
                                    {deliveryType === "productReturn"
                                        ? t("fields.returnPickupPoints", {ns: "common"})
                                        : t("fields.pickupPoints", {ns: "common"})}
                                </TableTitle>
                                <Table
                                    rowKey="_id"
                                    dataSource={pickupSteps}
                                    pagination={false}
                                    data-testid="collectingPointTable"
                                    expandable={{
                                        expandedRowRender: record => {
                                            return (
                                                <ProductTable
                                                    dataSource={record.products}
                                                    pagination={false}
                                                />
                                            );
                                        }
                                    }}
                                >
                                    <Column
                                        dataIndex="stepInfo"
                                        title={
                                            deliveryType === "productReturn"
                                                ? t("fields.customerName", {ns: "common"})
                                                : t("fields.warehouseName", {ns: "common"})
                                        }
                                        render={text => <span data-testid="name">{text.name}</span>}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={t("fields.phone", {ns: "common"})}
                                        render={text => (
                                            <span data-testid="phone">
                                                {formatPhoneNumber(text.phone)}
                                            </span>
                                        )}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={t("fields.address", {ns: "common"})}
                                        render={text => (
                                            <span data-testid="address">{text.address}</span>
                                        )}
                                    />
                                </Table>
                                {orderData.data.delivery.orders &&
                                orderData.data.delivery.orders[0] &&
                                orderData.data.delivery.orders[0].platformPickingTime ? (
                                    <PlatformDeliveryDetailTable
                                        orders={orderData.data.delivery.orders}
                                    />
                                ) : (
                                    ""
                                )}
                                {returnSteps.length >= 1 && (
                                    <>
                                        <TableTitle>
                                            {t("returnPoints", {ns: "orderDetail"})}
                                        </TableTitle>
                                        <Table
                                            rowKey="_id"
                                            dataSource={returnSteps}
                                            pagination={false}
                                            data-testid="returnSteps"
                                            expandable={{
                                                expandedRowRender: record => {
                                                    return (
                                                        <ProductTable
                                                            dataSource={record.stepInfo.products}
                                                            pagination={false}
                                                        />
                                                    );
                                                }
                                            }}
                                        >
                                            <Column
                                                dataIndex="stepInfo"
                                                title={
                                                    deliveryType === "productReturn"
                                                        ? t("fields.customerName", {ns: "common"})
                                                        : t("fields.warehouseName", {ns: "common"})
                                                }
                                                data-testid="name"
                                                render={text => text.name}
                                            />
                                            <Column
                                                dataIndex="stepInfo"
                                                title={t("fields.phone", {ns: "common"})}
                                                data-testid="phone"
                                                render={text => formatPhoneNumber(text.phone)}
                                            />
                                            <Column
                                                dataIndex="stepInfo"
                                                title={t("fields.address", {ns: "common"})}
                                                data-testid="address"
                                                render={text => text.address}
                                            />
                                        </Table>
                                    </>
                                )}*/

                                            orderData.data.courierLocation && (
                                                <>
                                                    <TableTitle>
                                                        {t("fields.courier", {ns: "common"})}
                                                    </TableTitle>
                                                    <DescriptionContainer
                                                        style={{
                                                            width: "100%"
                                                        }}
                                                        column={{xs: 1, sm: 1, md: 2}}
                                                        bordered
                                                    >
                                                        <DescriptionContainer.Item
                                                            label={t("fields.courierName", {
                                                                ns: "common"
                                                            })}
                                                        >
                                                            <span data-testid="courierName">
                                                                {
                                                                    orderData.data?.delivery
                                                                        ?.assignedCourier?.fullname
                                                                }
                                                            </span>
                                                        </DescriptionContainer.Item>

                                                        <DescriptionContainer.Item
                                                            label={t("fields.phone", {
                                                                ns: "common"
                                                            })}
                                                        >
                                                            <span data-testid="courierPhone">
                                                                {formatPhoneNumber(
                                                                    orderData.data?.delivery
                                                                        ?.assignedCourier?.cellPhone
                                                                )}
                                                            </span>
                                                        </DescriptionContainer.Item>
                                                    </DescriptionContainer>
                                                </>
                                            )}
                                            {/*stores.meStore.checkRole("notVisibleForCompanyUser") && (
                                    <>
                                        <TableTitle>
                                            {t("fields.company", {ns: "common"})}
                                        </TableTitle>
                                        <DescriptionContainer column={2} bordered>
                                            <DescriptionContainer.Item
                                                span={2}
                                                label={t("fields.companyName", {ns: "common"})}
                                                data-testid="companyName"
                                            >
                                                {orderData.data.delivery.company.companyName}
                                            </DescriptionContainer.Item>
                                            <DescriptionContainer.Item
                                                label={t("fields.createdDate", {ns: "common"})}
                                                data-testid="companyCreatedAt"
                                            >
                                                {moment(orderData.data.delivery.company.createdAt)
                                                    .locale(i18n.resolvedLanguage || "tr")
                                                    .format("ll")}
                                            </DescriptionContainer.Item>
                                        </DescriptionContainer>
                                    </>
                                )}
                            </> */}
                                        </Col>
                                        {transferData?.data?.length > 0 && (
                                            <TransferInfoContainer
                                                data-testid="transferInfoCard"
                                                style={{
                                                    width: "100%"
                                                }}
                                            >
                                                <TableTitle>
                                                    {t("transferHistory", {ns: "orderDetail"})}
                                                </TableTitle>
                                                <TransferLogList
                                                    itemLayout="vertical"
                                                    split={false}
                                                    loading={transferLoading}
                                                    dataSource={transferData.data}
                                                    renderItem={(item, index) => (
                                                        <>
                                                            <List.Item
                                                                data-testvalue={index}
                                                                data-testid="from"
                                                            >
                                                                <List.Item.Meta
                                                                    avatar={
                                                                        <Avatar
                                                                            size={50}
                                                                            data-testid="courierPhoto"
                                                                            src={
                                                                                item.fromCourier
                                                                                    ?.photo
                                                                                    ? `${config.getBasePublicUrl()}api/panel/v1/image/${
                                                                                          item
                                                                                              .fromCourier
                                                                                              ?.photo
                                                                                      }`
                                                                                    : placeholder
                                                                            }
                                                                        />
                                                                    }
                                                                    title={
                                                                        <a
                                                                            data-testid="courierName"
                                                                            href={`${config.getBasePublicUrl()}couriers/${
                                                                                item.fromCourier._id
                                                                            }`}
                                                                        >
                                                                            {
                                                                                item.fromCourier
                                                                                    ?.fullname
                                                                            }
                                                                        </a>
                                                                    }
                                                                    description={
                                                                        <>
                                                                            <Row data-testid="description">
                                                                                {index === 0 ? (
                                                                                    <TransferDescription>
                                                                                        <Text type="success">
                                                                                            {t(
                                                                                                "fields.start",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )}
                                                                                            :{" "}
                                                                                        </Text>
                                                                                        <span data-testid="startTime">
                                                                                            {moment(
                                                                                                orderData
                                                                                                    .data
                                                                                                    ?.delivery
                                                                                                    .createdAt
                                                                                            ).format(
                                                                                                "HH:mm:ss"
                                                                                            )}
                                                                                        </span>
                                                                                    </TransferDescription>
                                                                                ) : (
                                                                                    <TransferDescription>
                                                                                        <Text type="danger">
                                                                                            {" "}
                                                                                            Transfer:{" "}
                                                                                        </Text>
                                                                                        <span data-testid="transferTime">
                                                                                            {moment(
                                                                                                item?.createdAt
                                                                                            ).format(
                                                                                                "HH:mm:ss"
                                                                                            )}
                                                                                        </span>
                                                                                    </TransferDescription>
                                                                                )}
                                                                            </Row>
                                                                        </>
                                                                    }
                                                                />
                                                            </List.Item>
                                                            {index ===
                                                                transferData.data.length - 1 && (
                                                                <List.Item
                                                                    data-testvalue={index + 1}
                                                                    data-testid="to"
                                                                >
                                                                    <List.Item.Meta
                                                                        avatar={
                                                                            <Avatar
                                                                                data-testid="courierPhoto"
                                                                                size={50}
                                                                                src={
                                                                                    item.toCourier
                                                                                        ?.photo
                                                                                        ? `${config.getBasePublicUrl()}api/panel/v1/image/${
                                                                                              item
                                                                                                  .toCourier
                                                                                                  ?.photo
                                                                                          }`
                                                                                        : placeholder
                                                                                }
                                                                            />
                                                                        }
                                                                        title={
                                                                            <a
                                                                                data-testid="courierName"
                                                                                href={`${config.getBasePublicUrl()}couriers/${
                                                                                    item.toCourier
                                                                                        ._id
                                                                                }`}
                                                                            >
                                                                                {
                                                                                    item.toCourier
                                                                                        ?.fullname
                                                                                }
                                                                            </a>
                                                                        }
                                                                        description={
                                                                            <TransferDescription>
                                                                                <Text type="danger">
                                                                                    {" "}
                                                                                    Transfer:{" "}
                                                                                </Text>
                                                                                <span data-testid="transferTime">
                                                                                    {moment(
                                                                                        item?.createdAt
                                                                                    ).format(
                                                                                        "HH:mm:ss"
                                                                                    )}
                                                                                </span>
                                                                            </TransferDescription>
                                                                        }
                                                                    />
                                                                </List.Item>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </TransferInfoContainer>
                                        )}
                                    </Row>
                                </Col>
                            </MapStepRow>
                        </Col>
                    </Row>
                </React.Fragment>
            )}
        </Row>
    );
};
